import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/**
 * Registry through which the wizard and its hosted pages expose the handful
 * of setters the demo tour's auto-fill script drives (`demoScript.js`): the
 * wizard registers `wizard` (form patching, step navigation), the pages
 * register `dataCollection`, `modelSetup`, `dataEditor`, `train` while they
 * are mounted. Nothing here is demo-specific — it is a plain
 * "name → imperative API" map that the demo wizard (`DemoScope`) and the
 * live wizard with the Spark assistant open both provide; anywhere else
 * `useRegisterWizardApi` is a no-op and pages keep working untouched. Being
 * in the demo is a separate signal (`demoTourScope.js`).
 */
const WizardApiContext = createContext(null);

/** Registry names: who publishes what for the auto-fill script (`demoScript.js`). */
export const WIZARD_API = Object.freeze({
    /**
     * CreateTaskWizardPage: `setForm(patch)`, `createUsecase()`, `goToStep(key)`,
     * `setNextLoading(bool)` (the 1.5 s spinner on the page's Next button
     * while the tour turns the page by itself).
     */
    wizard: 'wizard',
    /**
     * DataCollectionPanel: `pickFiles(target, files)`, `patchDetails(target, patch)`,
     * `primaryTarget`, `next()`; for the Spark assistant also `getSnapshot()`,
     * `accept` and `openDataSource(source)` (the "Need a Dataset?" modals).
     */
    dataCollection: 'dataCollection',
    /** ModelSetupPage: `selectTeacher({ provider, model })`, `setSystemPrompt(text)`, `setUserPrompt(text)`, `setOutputFormat(schema)`. */
    modelSetup: 'modelSetup',
    /** ModelSetupStep: `next()` (validates, arms label generation, moves on). */
    modelSetupStep: 'modelSetupStep',
    /** DataSelection (Data Editor): `flipLabel(label?)`, `review()` on the first dataset row. */
    dataEditor: 'dataEditor',
    /** TrainFromSpecificUsecase: `markAdvancedTouched()`. */
    train: 'train',
});

export function WizardApiProvider({ children }) {
    // name → React ref holding the latest api object for that name.
    const refsRef = useRef(new Map());

    const register = useCallback((name, ref) => {
        refsRef.current.set(name, ref);
        return () => {
            if (refsRef.current.get(name) === ref) refsRef.current.delete(name);
        };
    }, []);

    const get = useCallback((name) => refsRef.current.get(name)?.current || null, []);

    const value = useMemo(() => ({ register, get }), [register, get]);
    return <WizardApiContext.Provider value={value}>{children}</WizardApiContext.Provider>;
}

/** The registry (`{ register, get }`) or null outside a `WizardApiProvider`. */
export const useWizardApiRegistry = () => useContext(WizardApiContext);

/**
 * Publish `api` under `name` for as long as the component is mounted. The
 * registry reads the latest `api` on every `get`, so callers can pass a fresh
 * object literal every render without stale closures.
 */
export function useRegisterWizardApi(name, api) {
    const registry = useContext(WizardApiContext);
    const apiRef = useRef(api);
    apiRef.current = api;

    useEffect(() => {
        if (!registry) return undefined;
        return registry.register(name, apiRef);
    }, [registry, name]);
}

/**
 * `useRegisterWizardApi` as an element, for a component that renders the
 * provider itself and so cannot call the hook inside it (the wizard page).
 */
export function RegisterWizardApi({ name, api }) {
    useRegisterWizardApi(name, api);
    return null;
}
